import { useState } from 'react'
import {
  Panel, SectionHeader, ErpTable, Th, Td, EmptyRow, KpiStrip, MetricCard,
} from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { FilterBar } from '@/shared/ui/filters'
import { Modal, Field } from '@/shared/ui/Modal'
import { explain } from '@/shared/api/problem'
import {
  checkinCounts, checkinStates, currentQueueReports, listCells, listRecentCheckins,
  queueBands, stationIssues, submitQueueReport,
} from './api'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'
import { label } from './vocab'

/**
 * Polling day — live check-in feed, per-state counters, station queue board. (§16.3 /live)
 *
 * ## Two bugs, one screen
 *
 * The page called `listCheckins(electionId)` → `GET /turnout/checkins?electionId=…`. That
 * route answers "what is THIS voter's state" and requires a `voterId`, so every call was
 * a 400 that the page then tried to `.map`. There was no election-wide check-in read at
 * all; `/checkins/recent` and `/checkins/counts` are new.
 *
 * Second, the loader's dependency array was `[]`. `electionId` is `null` on first render
 * while the election list loads, so the loader ran once against the `Promise.resolve([])`
 * branch and `useCallback` never rebuilt it. Even with a working route the screen could
 * not have filled, and switching election did nothing.
 *
 * W3 adds the offline outbox and the full live map. The escalation queue needs an
 * escalation endpoint, which does not exist yet — no placeholder for it here.
 */
export function LiveOpsPage() {
  const canRead = useCan('turnout:read')
  const selection = useElection()
  const { electionId } = selection
  const [state, setState] = useState('')
  const [reporting, setReporting] = useState(false)

  const states = useLoad(checkinStates, [])
  const counts = useLoad(
    () => (electionId ? checkinCounts(electionId) : Promise.resolve<Record<string, number>>({})),
    [electionId])
  const checkins = useLoad(
    () => (electionId
      ? listRecentCheckins(electionId, { state: state || undefined, limit: 100 })
      : Promise.resolve([])),
    [electionId, state])
  const queues = useLoad(
    () => (electionId ? currentQueueReports(electionId) : Promise.resolve([])),
    [electionId])

  if (!canRead) return <Forbidden what="Polling day live ops" />

  const rows = checkins.data ?? []
  const queueRows = queues.data ?? []
  // Counted by the database over every live row, not over the capped feed above — a
  // hundred-row window cannot tell you how many people voted.
  const c = counts.data ?? {}
  const n = (code: string) => (c[code] ?? 0).toLocaleString()

  return (
    <div className="space-y-4">
      <SectionHeader title="Polling day"
        description="Live check-ins, station queues and the states behind them."
        right={(
          <span className="flex items-center gap-2">
            <ElectionPicker selection={selection} />
            {/*
               The station-queue table is fed by POST /turnout/queue-reports, and nothing
               in this application submitted one — so the table was permanently empty in
               any real deployment and nothing said why. This is that control.
            */}
            <button type="button" className="erp-btn erp-btn-primary h-9"
              disabled={!electionId} onClick={() => setReporting(true)}>
              Report a queue
            </button>
          </span>
        )}
      />
      <ErrorStrip
        message={selection.error ?? counts.error ?? checkins.error ?? queues.error} />

      <KpiStrip>
        <MetricCard label="Voted" value={n('VOTED')} sub="confirmed" accent />
        <MetricCard label="In queue" value={n('IN_QUEUE')} sub="at a station now" />
        <MetricCard label="On the way" value={n('EN_ROUTE')} sub="reported en route" />
        <MetricCard label="Turned away" value={n('TURNED_AWAY')} sub="needs following up" />
        <MetricCard label="Unreachable" value={n('UNREACHABLE')} sub="no contact made" />
      </KpiStrip>

      <Panel title="Station queues"
        description="How long the line is at each station right now, worst first. One row per station — the latest report. A station nobody has reported on is not listed, because an absent report is not the same as a short queue.">
        {queues.loading && queueRows.length === 0 ? <LoadingBlock label="Loading queues…" /> : (
          <ErpTable head={(
            <>
              <Th>Station</Th>
              <Th>Queue</Th>
              <Th className="text-right">Est. wait</Th>
              <Th className="text-right">Est. people</Th>
              <Th>Open</Th>
              <Th>Issues</Th>
              <Th>Reported</Th>
            </>
          )}>
            {queueRows.length === 0 ? (
              <EmptyRow colSpan={7}
                message="No queue reports for this election yet. Use the Report a queue button above, or a station captain submits one from the field app." />
            ) : queueRows.map((q) => (
              <tr key={q.id} className="hover:bg-muted/40">
                <Td>
                  <span className="block font-medium">
                    {q.stationName ?? 'Station no longer in the register'}
                  </span>
                  <span className="block font-mono text-erp-xs text-muted-foreground">
                    {q.stationId.slice(0, 8)}
                  </span>
                </Td>
                <Td>
                  <span className={q.queueBandCode === 'VERY_LONG' || q.queueBandCode === 'LONG'
                    ? 'text-amber-700 font-medium' : ''}>
                    {label(q.queueBandCode)}
                  </span>
                </Td>
                <Td className="text-right tabular-nums">
                  {q.estimatedWaitMin == null ? '—' : `${q.estimatedWaitMin} min`}
                </Td>
                <Td className="text-right tabular-nums">
                  {q.estimatedPeople == null ? '—' : q.estimatedPeople.toLocaleString()}
                </Td>
                <Td>
                  {q.stationOpen
                    ? <span className="text-muted-foreground">Open</span>
                    : <span className="text-red-700 font-medium">Closed</span>}
                </Td>
                <Td className="text-muted-foreground">{issueList(q.issues)}</Td>
                <Td className="text-muted-foreground">
                  {new Date(q.reportedAt).toLocaleTimeString()}
                </Td>
              </tr>
            ))}
          </ErpTable>
        )}
      </Panel>

      <FilterBar>
        <label className="flex items-center gap-1.5 text-erp-xs">
          State
          <select className="erp-input h-9" value={state}
            disabled={states.loading}
            onChange={(e) => setState(e.target.value)}>
            <option value="">All states</option>
            {(states.data ?? []).map((s) => (
              <option key={s} value={s}>{label(s)}</option>
            ))}
          </select>
        </label>
      </FilterBar>

      <Panel title="Recent check-ins"
        description="The last 100 live reports, newest first. Times are the DEVICE clock — the one velocity is measured on.">
        {checkins.loading && rows.length === 0 ? <LoadingBlock label="Loading check-ins…" /> : (
          <ErpTable head={(
            <>
              <Th>Voter</Th>
              <Th>Station</Th>
              <Th>State</Th>
              <Th>Confidence</Th>
              <Th>Recorded</Th>
            </>
          )}>
            {rows.length === 0 ? (
              <EmptyRow colSpan={5}
                message="No check-ins yet. Field staff report them from the coordinator board." />
            ) : rows.map((r) => (
              <tr key={r.id} className="hover:bg-muted/40">
                {/* Names, not uuid slices. Eight hex characters identify nobody: a
                    coordinator reading this feed cannot tell whether a row is the person
                    they just called. The server resolves both in one batched lookup each,
                    and masks the name to initials without turnout:read-raw. */}
                <Td>
                  <span className="block">{r.voterDisplayName ?? '—'}</span>
                  <span className="block font-mono text-erp-xs text-muted-foreground">
                    {r.voterId?.slice(0, 8)}
                  </span>
                </Td>
                <Td>
                  {r.stationName
                    ?? <span className="font-mono text-erp-xs">{r.stationId?.slice(0, 8)}</span>}
                </Td>
                <Td>{label(r.stateCode)}</Td>
                <Td className={r.confidenceCode === 'DISPUTED'
                  ? 'text-red-700 font-medium' : 'text-muted-foreground'}>
                  {label(r.confidenceCode)}
                </Td>
                <Td className="text-muted-foreground">
                  {new Date(r.clientRecordedAt).toLocaleTimeString()}
                </Td>
              </tr>
            ))}
          </ErpTable>
        )}
      </Panel>
      {reporting && electionId && (
        <QueueReportModal
          electionId={electionId}
          onClose={() => setReporting(false)}
          onDone={() => { setReporting(false); queues.reload() }}
        />
      )}
    </div>
  )
}

/**
 * Submit a queue report.
 *
 * A report is IMMUTABLE — there is no edit. Submitting again writes a new row and the
 * newest one becomes the station's current state, which is why the history is worth
 * keeping: the shape of a queue over the day is what velocity analysis reads.
 */
function QueueReportModal({ electionId, onClose, onDone }: {
  electionId: string
  onClose: () => void
  onDone: () => void
}) {
  const bands = useLoad(queueBands, [])
  const issues = useLoad(stationIssues, [])
  /*
     Stations come from THIS election's cells, not from `/geography/units`.

     The geography list is gated on `geography:read` — a different feature's authority. A
     turnout operator holding the whole `turnout:*` set does not necessarily have it, so
     the call 403'd, `stations.data` stayed null, and the dropdown rendered empty with
     nothing saying why. The cells list needs only `turnout:read`, which this screen
     already requires, and it carries the station's name AND its ltree path — so the
     station and the scope path the report is filed under can no longer disagree.
  */
  const cells = useLoad(
    () => listCells({ electionId, status: 'ACTIVE' }), [electionId])
  const [stationId, setStationId] = useState('')
  const [band, setBand] = useState('')
  const [waitMin, setWaitMin] = useState('')
  const [people, setPeople] = useState('')
  const [stationOpen, setStationOpen] = useState(true)
  const [picked, setPicked] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // One entry per station, even when several cells vote at it.
  const stationRows = Array.from(
    new Map((cells.data ?? []).map((c) => [c.stationId, c])).values())
  const station = stationRows.find((c) => c.stationId === stationId)

  const toggle = (code: string) => setPicked((prev) =>
    prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code])

  const submit = async () => {
    if (!stationId || !band || !station) {
      // Say why rather than returning in silence — a submit button that does nothing and
      // explains nothing is the worst failure mode a form has.
      setError('Choose a station and a queue band first.')
      return
    }
    setBusy(true); setError(null)
    try {
      await submitQueueReport({
        electionId,
        stationId,
        // The cell's own ltree path — the scope every downstream rollup groups on.
        unitPath: station.unitPath,
        queueBandCode: band,
        estimatedWaitMin: waitMin === '' ? null : Number(waitMin),
        estimatedPeople: people === '' ? null : Number(people),
        stationOpen,
        issueCodes: picked,
        note: note || null,
      })
      onDone()
    } catch (e) { setError(explain(e)); setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} width="lg" title="Report a queue"
      description="What the line looks like at one station, right now. Reports are never edited — a later one supersedes this."
      footer={(
        <>
          <button type="button" className="erp-btn h-9" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary h-9"
            disabled={busy || !stationId || !band} onClick={submit}>
            {busy ? 'Submitting…' : 'Submit'}
          </button>
        </>
      )}>
      <ErrorStrip message={error ?? cells.error} />
      <div className="space-y-3">
        <Field label="Polling station" required
          hint={!cells.loading && stationRows.length === 0
            ? 'No active cells in this election, so there is no station to report on yet.'
            : undefined}>
          <select className="erp-input h-9 w-full" value={stationId}
            disabled={cells.loading}
            onChange={(e) => setStationId(e.target.value)}>
            <option value="">
              {cells.loading ? 'Loading stations…' : 'Choose a station…'}
            </option>
            {stationRows.map((c) => (
              <option key={c.stationId} value={c.stationId}>
                {c.stationName ?? c.stationId.slice(0, 8)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Queue"
          required
          hint="How long the line is, in bands rather than a count nobody can take accurately.">
          <select className="erp-input h-9 w-full" value={band}
            disabled={bands.loading}
            onChange={(e) => setBand(e.target.value)}>
            <option value="">Choose…</option>
            {(bands.data ?? []).map((b) => (
              <option key={b} value={b}>{label(b)}</option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Estimated wait (minutes)">
            <input className="erp-input h-9 w-full" type="number" min="0" value={waitMin}
              onChange={(e) => setWaitMin(e.target.value)} />
          </Field>
          <Field label="Estimated people">
            <input className="erp-input h-9 w-full" type="number" min="0" value={people}
              onChange={(e) => setPeople(e.target.value)} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-erp-sm">
          <input type="checkbox" checked={stationOpen}
            onChange={(e) => setStationOpen(e.target.checked)} />
          The station is open
        </label>
        <Field label="Issues"
          hint="What is going wrong, if anything. Several can apply at once.">
          <div className="flex flex-wrap gap-2">
            {(issues.data ?? []).map((c) => (
              <button key={c} type="button"
                className={picked.includes(c) ? 'erp-btn erp-btn-primary h-8' : 'erp-btn h-8'}
                onClick={() => toggle(c)}>
                {label(c)}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Note">
          <textarea className="erp-input w-full min-h-16" value={note} maxLength={512}
            onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}

/** `issues` arrives as a JSON string; a malformed one renders as nothing, never a crash. */
function issueList(raw: string | null): string {
  if (!raw) return '—'
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.length === 0) return '—'
    return parsed.map((c) => label(String(c))).join(', ')
  } catch { return '—' }
}
